/** Browser-side helpers for the site-photo uploader: the EXIF date-taken of a
 *  JPEG, a canvas thumbnail, an XHR PUT with progress (fetch has no upload
 *  progress), a small concurrency runner and the local-day key the server
 *  groups sets on — plus the naming and save-to-disk half of downloads.
 *  Nothing here talks to the API. */

export const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";
export const PHOTO_MAX_BYTES = 30 * 1024 * 1024;
/** Upload-init takes at most this many files per call. */
export const PHOTO_BATCH_SIZE = 25;
/** Long edge of the thumbnail the browser makes (JPEG, q 0.82 — ~40-60 KB). */
export const THUMB_MAX_EDGE = 512;

/** iOS hands the picker's HEIC over as JPEG when HEIC isn't in `accept`, and
 *  Chrome can't decode HEIC on a canvas at all — so the accept list is JPEG /
 *  PNG / WebP on purpose. */
export function isAcceptedPhoto(file: File): boolean {
  return file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
}

/* ── EXIF ─────────────────────────────────────────────────────────────── */

interface IfdEntry {
  tag: number;
  type: number;
  count: number;
  valueAt: number;
}

function readIfd(view: DataView, start: number, le: boolean): IfdEntry[] {
  const out: IfdEntry[] = [];
  if (start < 0 || start + 2 > view.byteLength) return out;
  const n = view.getUint16(start, le);
  for (let i = 0; i < n; i += 1) {
    const e = start + 2 + i * 12;
    if (e + 12 > view.byteLength) break;
    out.push({
      tag: view.getUint16(e, le),
      type: view.getUint16(e + 2, le),
      count: view.getUint32(e + 4, le),
      valueAt: e + 8,
    });
  }
  return out;
}

function readAscii(view: DataView, tiff: number, entry: IfdEntry, le: boolean): string | null {
  const at = entry.count > 4 ? tiff + view.getUint32(entry.valueAt, le) : entry.valueAt;
  if (at < 0 || at + entry.count > view.byteLength) return null;
  let s = "";
  for (let i = 0; i < entry.count; i += 1) {
    const c = view.getUint8(at + i);
    if (c === 0) break;
    s += String.fromCharCode(c);
  }
  return s;
}

/** "YYYY:MM:DD HH:MM:SS" (no zone — the camera's local time) → epoch ms in
 *  this browser's zone, which for a studio photographing UK sites is the same
 *  clock. */
function parseExifDate(s: string | null): number | null {
  const m = s ? /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(s) : null;
  if (!m) return null;
  const t = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime();
  return Number.isFinite(t) && t > 0 ? t : null;
}

function parseTiffDate(view: DataView, tiff: number): number | null {
  if (tiff + 8 > view.byteLength) return null;
  const le = view.getUint16(tiff) === 0x4949;
  if (view.getUint16(tiff + 2, le) !== 0x2a) return null;
  const ifd0 = readIfd(view, tiff + view.getUint32(tiff + 4, le), le);
  const exifPtr = ifd0.find((e) => e.tag === 0x8769);
  if (exifPtr) {
    const exif = readIfd(view, tiff + view.getUint32(exifPtr.valueAt, le), le);
    // DateTimeOriginal, else DateTimeDigitized.
    const dto = exif.find((e) => e.tag === 0x9003 && e.type === 2) ?? exif.find((e) => e.tag === 0x9004 && e.type === 2);
    const t = dto ? parseExifDate(readAscii(view, tiff, dto, le)) : null;
    if (t) return t;
  }
  // IFD0 DateTime (last modified in-camera) as a last resort.
  const dt = ifd0.find((e) => e.tag === 0x0132 && e.type === 2);
  return dt ? parseExifDate(readAscii(view, tiff, dt, le)) : null;
}

/** EXIF DateTimeOriginal of a JPEG as local-time epoch ms; null when the file
 *  isn't a JPEG or carries no usable date. Reads only the first 256 KB — the
 *  APP1 segment sits right after SOI. Never throws. */
export async function readExifTakenAt(file: File): Promise<number | null> {
  if (file.type !== "image/jpeg") return null;
  try {
    const buf = await file.slice(0, 256 * 1024).arrayBuffer();
    const view = new DataView(buf);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;
    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset);
      if (marker === 0xffda || marker === 0xffd9) break; // SOS / EOI — no EXIF past here
      const size = view.getUint16(offset + 2);
      if (size < 2) return null;
      if (
        marker === 0xffe1 &&
        offset + 10 <= view.byteLength &&
        view.getUint32(offset + 4) === 0x45786966 && // "Exif"
        view.getUint16(offset + 8) === 0
      ) {
        return parseTiffDate(view, offset + 10);
      }
      offset += 2 + size;
    }
  } catch {
    /* unreadable — caller falls back to the file's modified time */
  }
  return null;
}

/* ── Thumbnail ────────────────────────────────────────────────────────── */

export interface PreparedPhoto {
  /** Oriented pixel size of the original (browsers apply EXIF orientation to
   *  the decoded image, so these are the as-displayed dimensions). */
  width: number;
  height: number;
  thumb: Blob;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not read the image."));
    img.src = url;
  });
}

/** Decode the photo and draw a JPEG thumbnail with its long edge at `maxEdge`.
 *  Decoding goes through an <img> (not createImageBitmap) so every current
 *  browser applies the EXIF orientation for us. */
export async function prepareThumbnail(file: File, maxEdge = THUMB_MAX_EDGE): Promise<PreparedPhoto> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) throw new Error("Could not read the image.");
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const tw = Math.max(1, Math.round(w * scale));
    const th = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable.");
    ctx.drawImage(img, 0, 0, tw, th);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob) throw new Error("Could not make a thumbnail.");
    return { width: w, height: h, thumb: blob };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* ── Transport ────────────────────────────────────────────────────────── */

/** PUT a blob to a presigned R2 URL with upload progress. Resolves with the
 *  ETag R2 returned (the bucket's CORS exposes it; "" if absent). The URL is
 *  signed for host only with an unsigned payload, so Content-Type is free to
 *  set — and R2 stores it as the object's type. */
export function putWithProgress(
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.total > 0 ? e.loaded / e.total : 0);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve((xhr.getResponseHeader("ETag") ?? "").replace(/"/g, ""));
      } else {
        reject(new Error(`Upload failed (${xhr.status}).`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed — network error."));
    xhr.onabort = () => reject(new Error("Upload cancelled."));
    xhr.send(body);
  });
}

/** Run `fn` over `items` with at most `limit` in flight, preserving order. */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function worker() {
    for (;;) {
      const i = next;
      next += 1;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/* ── Days ─────────────────────────────────────────────────────────────── */

/** Local calendar day "YYYY-MM-DD" of an epoch ms — the server groups
 *  auto-created sets on this, so the day is decided where the photo was
 *  taken, not by the server's UTC clock. */
export function localDayKey(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "Thu 12 Mar 2026" — the day headers inside a set. */
export function formatDayHeading(ms: number): string {
  return new Date(ms).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

/** "14:32" — the time under a thumbnail in the lightbox. */
export function formatTakenTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/* ── Download ─────────────────────────────────────────────────────────── */

/** Characters no filesystem (or unzip) will take in a name, plus controls. */
// eslint-disable-next-line no-control-regex
const UNSAFE_NAME = /[\\/:*?"<>|\u0000-\u001f]/g;

/** A label or file name made safe to use as one path segment on disk. */
export function safeFileName(name: string): string {
  return name.replace(UNSAFE_NAME, "-").replace(/\s+/g, " ").trim();
}

/** The name a photo downloads as: its original file name, made safe, with an
 *  extension from its type when the name has none (URL imports can arrive
 *  without one). */
export function photoFileName(photo: { id: number; file_name: string; mime_type: string }): string {
  const base = safeFileName(photo.file_name) || `photo-${photo.id}`;
  if (/\.[a-z0-9]{2,5}$/i.test(base)) return base;
  return base + (photo.mime_type === "image/png" ? ".png" : photo.mime_type === "image/webp" ? ".webp" : ".jpg");
}

/** "Site visit 12 Mar" → "site-visit-12-mar" — the set part of a zip name. */
export function fileSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Hand a Blob to the browser as a file download. The object URL is revoked
 *  after a minute rather than straight away — Safari can still be reading a
 *  large blob when click() returns. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
